import { useDeleteDraftMutation } from '@carboncopyco/data';
import {
  BackdropOverlay,
  EditorSidebar,
  useEditorTools,
} from '@carboncopyco/editor-sdk';
import type { IEditorDraft, IOrganizationData } from '@carboncopyco/types';
import type { IGlobalTab } from '@carboncopyco/types/desktop';
import { Loader as AppLoader } from '@carboncopyco/ui';
import { useEffect } from 'react';
import { EditorModals, EditorNavbar, ExitEditorDialog } from '.';
import { useFocusAppService } from '../../hooks/useFocusAppService';
import { useTabFocus } from '../../hooks/useTabFocus';
import { useTabStateCallback } from '../../hooks/useTabStateCallback';
import { isActiveOrAllInnerTabsLoaded } from '../../shared/utils';
import { useTabs } from '../../store/context';
import { ResponsiveModeContextProvider } from '../../store/context/ResponsiveModeContext';
import InnerTabsContainer from '../InnerTabs/InnerTabsContainer';
import InnerViewArea from '../InnerViewArea';
import { useEditorPageInfo } from './hooks';

interface EditorContainerProps {
  organization: IOrganizationData;
  draft?: IEditorDraft;
  isFetchingDraft: boolean;
  tab: IGlobalTab;
  onExit: () => void;
  updateDemo?: boolean;
}

export const EditorContainer = ({
  organization,
  draft,
  isFetchingDraft,
  tab,
  onExit,
  updateDemo = false,
}: EditorContainerProps) => {
  const { triggerFocus } = useFocusAppService();
  const pageInfo = useEditorPageInfo(tab);

  const [deleteDraft] = useDeleteDraftMutation();

  const resetChanges = async () => {
    const draftId = (draft?.id || '').toString();
    const organizationId = organization.id.toString();
    try {
      await deleteDraft({
        organizationId,
        draftId,
      });
    } catch (e) {
      console.error('Error deleting draft while exiting editor', e);
    }
  };

  const {
    isOpenCloseTabDialog,
    setIsOpenCloseTabDialog,
    activeGlobalTabId,
    setIsModalOpenByTabId,
  } = useTabs();

  const {
    isOpen: isOpenModal,
    close: closeModal,
    isOpenAny: isAnyModalOpen,
    closeAll: closeAllModals,
  } = useEditorTools();

  useTabFocus({
    tab,
    onTabIsDeactivated: () => {
      closeAllModals();
      setIsOpenCloseTabDialog(false);
    },
  });

  const isFindReplaceModalOpen = isOpenModal('findReplace');
  const isVariableModalOpen = isOpenModal('variable');

  const syncAppFocus = () => {
    triggerFocus(isAnyModalOpen || isOpenCloseTabDialog);
    setIsModalOpenByTabId(activeGlobalTabId, isAnyModalOpen);
  };

  useEffect(syncAppFocus, [isAnyModalOpen]);

  const { isActiveTab } = useTabStateCallback();

  const isInnerTabLoaded = isActiveOrAllInnerTabsLoaded(tab);

  const handleExit = async (checkIsDraftModified = true) => {
    if (checkIsDraftModified && draft?.modified) {
      await resetChanges();
    }
    onExit();
  };

  useEffect(() => {
    if (isActiveTab) closeAllModals();
  }, [isActiveTab, tab.activeInnerTabId]);

  return (
    <ResponsiveModeContextProvider>
      <EditorNavbar
        pageInfo={pageInfo}
        draft={draft}
        publishDiscardDisabled={!draft?.modified || isFetchingDraft}
        tab={tab}
        onExit={onExit}
      />
      <InnerTabsContainer globalTab={tab} />

      <div className={`flex grow${isInnerTabLoaded ? '' : ' bg-white'}`}>
        <EditorSidebar />
        <div className="flex flex-col flex-1 relative overflow-hidden">
          <div className="flex flex-col min-w-0 flex-1 relative">
            <BackdropOverlay />
            <InnerViewArea
              tab={tab}
              deps={[isFindReplaceModalOpen, isVariableModalOpen]}
            />
            {!isInnerTabLoaded && <AppLoader show />}
          </div>
          <EditorModals
            isOpenModal={isOpenModal}
            closeModal={closeModal}
            draft={draft}
            tab={tab}
            onExit={() => {
              handleExit(false);
            }}
            isInnerTabLoaded={isInnerTabLoaded}
            organizationId={String(organization.id)}
          />
        </div>
      </div>

      <ExitEditorDialog
        isOpen={isOpenCloseTabDialog}
        onClose={() => setIsOpenCloseTabDialog(false)}
        onExit={handleExit}
        isUpdate={updateDemo}
      />
    </ResponsiveModeContextProvider>
  );
};
